/**
 * Shared BroadcastChannel for syncing state between the main app window
 * and tool windows (SPARQL editor, Rule Editor, Ontology Editor/Viewer).
 */

import { useState, useEffect, useRef } from 'react'
import { publishDict, GLOBAL_NAMESPACE } from '../redux/pubsub'
import { store } from '../store'

export type ToolSyncMessage =
  | { type: 'repo-update'; repoURL: string; localRepoURL?: string }
  | { type: 'request-repo' }
  | { type: 'repo-response'; repoURL: string; localRepoURL?: string }
  | { type: 'pubsub-update'; vars: Record<string, unknown> }
  | { type: 'request-pubsub' }
  | { type: 'pubsub-response'; vars: Record<string, unknown> }

const CHANNEL_NAME = 'tool-sync'

let _channel: BroadcastChannel | null = null

export function getToolSyncChannel(): BroadcastChannel {
  if (!_channel) {
    _channel = new BroadcastChannel(CHANNEL_NAME)
  }
  return _channel
}

export function useToolSyncPubsub() {
  useEffect(() => {
    const ch = getToolSyncChannel()

    const handler = (e: MessageEvent<ToolSyncMessage>) => {
      if (e.data.type === 'pubsub-update' || e.data.type === 'pubsub-response') {
        publishDict(e.data.vars, GLOBAL_NAMESPACE, 'tool-sync:pubsub')
      }
    }

    ch.addEventListener('message', handler)
    ch.postMessage({ type: 'request-pubsub' } satisfies ToolSyncMessage)

    return () => ch.removeEventListener('message', handler)
  }, [])
}

const isStaticApp = () => Boolean((window as any).CONNECTED_APPS_STATIC)

export function initToolSyncBroadcaster(): () => void {
  if (isStaticApp()) return () => {} // Lock&Published apps have no tool windows to sync with

  const ch = getToolSyncChannel()
  const getVars = () => stripNonSerializable(store.getState().rootAppVars_INTERNAL ?? {})

  const handleMessage = (e: MessageEvent<ToolSyncMessage>) => {
    if (e.data.type === 'request-pubsub') {
      ch.postMessage({ type: 'pubsub-response', vars: getVars() } satisfies ToolSyncMessage)
    }
  }
  ch.addEventListener('message', handleMessage)

  let prev = store.getState().rootAppVars_INTERNAL
  const unsubscribe = store.subscribe(() => {
    const next = store.getState().rootAppVars_INTERNAL
    if (next !== prev) {
      prev = next
      ch.postMessage({ type: 'pubsub-update', vars: stripNonSerializable(next ?? {}) } satisfies ToolSyncMessage)
    }
  })

  return () => {
    unsubscribe()
    ch.removeEventListener('message', handleMessage)
  }
}

/** BroadcastChannel can only send structured-cloneable data — strip Symbols */
function stripNonSerializable(vars: Record<string | symbol, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {}
  for (const key of Object.keys(vars)) {
    result[key] = vars[key]
  }
  return result
}

/**
 * React hook for child/tool windows to subscribe to repoURL updates
 * from the main app. Falls back to the initial values from URL params.
 *
 * On mount, it sends a `request-repo` message to the main app.
 * The main app responds with `repo-response`, and also broadcasts
 * `repo-update` when the repoURL changes.
 */
export function useToolSyncRepo(initialRepoURL: string, initialLocalRepoURL?: string) {
  const [repoURL, setRepoURL] = useState(initialRepoURL)
  const [localRepoURL, setLocalRepoURL] = useState(initialLocalRepoURL)

  useEffect(() => {
    const ch = getToolSyncChannel()

    const handler = (e: MessageEvent<ToolSyncMessage>) => {
      if (e.data.type === 'repo-update' || e.data.type === 'repo-response') {
        setRepoURL(e.data.repoURL)
        if (e.data.localRepoURL !== undefined) setLocalRepoURL(e.data.localRepoURL)
      }
    }

    ch.addEventListener('message', handler)

    // Request current repo from the main app
    ch.postMessage({ type: 'request-repo' } satisfies ToolSyncMessage)

    return () => ch.removeEventListener('message', handler)
  }, [])

  return { repoURL, localRepoURL }
}
